// src/Views/IgnisLanding/IgnisLanding.js
// 🔥 IGNIS — Public landing page for SEO + marketing
// Accessible at /ignis — no auth required

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './IgnisLanding.css';

const IgnisLanding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const features = [
    {
      icon: '🤖',
      title: 'Fully Automated',
      desc: 'Ignis operates 24/7 on your Bybit account. No manual intervention needed — just connect and let the AI agent work.'
    },
    {
      icon: '🧠',
      title: 'Multi-Strategy AI',
      desc: 'Ignis runs multiple AI-driven strategies using technical indicators, trend detection, and real-time market analysis.'
    },
    {
      icon: '🔒',
      title: 'Your Keys, Your Funds',
      desc: 'Ignis connects via API keys. Your funds never leave your Bybit account. Full control stays with you.'
    },
    {
      icon: '🎯',
      title: 'AI Position Sizing',
      desc: 'Dynamic position sizing adapts to market conditions — maximizing opportunity while managing risk intelligently.'
    },
    {
      icon: '📊',
      title: 'Server-Side Take Profit',
      desc: 'Take-profit logic runs server-side for faster execution and zero missed exits — even if your device is offline.'
    },
    {
      icon: '💬',
      title: 'Telegram Community',
      desc: 'Every subscriber gets access to our private Telegram group for real-time updates, alerts, and support.'
    }
  ];

  const tiers = [
    {
      name: 'Bronze',
      price: '$500',
      color: '#CD7F32',
      highlights: [
        'Automated Bybit trading',
        'Multi-strategy AI access',
        'Indicator-based execution',
        'Server-side take profit',
        'Telegram community access',
        'Standard allocation tier'
      ]
    },
    {
      name: 'Silver',
      price: '$1,000',
      color: '#C0C0C0',
      popular: true,
      highlights: [
        'Everything in Bronze',
        'Increased capital allocation',
        'Priority AI agent execution',
        'Enhanced AI position sizing',
        'Detailed trade analytics',
        'Priority support'
      ]
    },
    {
      name: 'Gold',
      price: '$2,000',
      color: '#FFD700',
      highlights: [
        'Everything in Silver',
        'Maximum capital allocation',
        'Highest priority execution',
        'Advanced risk management',
        'Direct support channel',
        'Early access to new strategies'
      ]
    }
  ];

  const steps = [
    { step: '01', title: 'Sign Up', desc: 'Create your AiPredict account and choose your Ignis tier.' },
    { step: '02', title: 'Connect Bybit', desc: 'Link your Bybit account with read/trade API keys. Withdrawal stays disabled — your funds are always safe.' },
    { step: '03', title: 'Ignis AI Agent Activates', desc: 'The Ignis AI agent begins executing trades automatically. Monitor performance from your dashboard in real time.' },
    { step: '04', title: 'Watch It Grow', desc: 'Ignis works around the clock. Review trades, track profits, and adjust your tier as you scale.' }
  ];

  return (
    <div className="ignis-landing">

      {/* ===== HERO ===== */}
      <section className="ignis-hero">
        <div className="ignis-hero__badge">🔥 NOW LIVE</div>
        <img 
          src="/ignis-logo-full.png" 
          alt="Ignis — AI Crypto Trading Agent by AiPredict" 
          className="ignis-hero__logo"
        />
        <h1 className="ignis-hero__title">
          Meet <span className="ignis-hero__highlight">Ignis</span>
        </h1>
        <p className="ignis-hero__subtitle">
          Autonomous AI crypto trading agent that never sleeps.
        </p>
        <p className="ignis-hero__desc">
          Ignis connects to your Bybit account and trades automatically using AI-driven strategies built on institutional price structure analysis, multi-timeframe confluence, and real-time market intelligence. 
          No charts to watch. No signals to follow. Just results.
        </p>
        <div className="ignis-hero__actions">
          <button className="ignis-btn ignis-btn--primary" onClick={() => navigate(user ? '/crypto' : '/signin')}>
            Get Started
          </button>
          <button className="ignis-btn ignis-btn--secondary" onClick={() => document.getElementById('ignis-pricing').scrollIntoView({ behavior: 'smooth' })}>
            View Plans
          </button>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section className="ignis-features">
        <h2 className="ignis-section__title">Why Ignis?</h2>
        <p className="ignis-section__subtitle">Hands-free crypto trading, powered by an autonomous AI agent — not indicators on a chart.</p>
        <div className="ignis-features__grid">
          {features.map((f, i) => (
            <div className="ignis-feature-card" key={i}>
              <div className="ignis-feature-card__icon">{f.icon}</div>
              <h3 className="ignis-feature-card__title">{f.title}</h3>
              <p className="ignis-feature-card__desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="ignis-how">
        <h2 className="ignis-section__title">How It Works</h2>
        <p className="ignis-section__subtitle">From sign-up to automated trading in minutes.</p>
        <div className="ignis-how__steps">
          {steps.map((s, i) => (
            <div className="ignis-step" key={i}>
              <div className="ignis-step__number">{s.step}</div>
              <h3 className="ignis-step__title">{s.title}</h3>
              <p className="ignis-step__desc">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section className="ignis-pricing" id="ignis-pricing">
        <h2 className="ignis-section__title">Choose Your Tier</h2>
        <p className="ignis-section__subtitle">All plans include full AI agent access. Higher tiers unlock greater capital allocation.</p>
        <div className="ignis-pricing__grid">
          {tiers.map((t, i) => (
            <div className={`ignis-tier-card ${t.popular ? 'ignis-tier-card--popular' : ''}`} key={i}>
              {t.popular && <div className="ignis-tier-card__badge">Most Popular</div>}
              <div className="ignis-tier-card__name" style={{ color: t.color }}>{t.name}</div>
              <div className="ignis-tier-card__price">
                {t.price}<span className="ignis-tier-card__period">/mo</span>
              </div>
              <ul className="ignis-tier-card__list">
                {t.highlights.map((h, j) => (
                  <li key={j}>
                    <span className="ignis-tier-card__check">✓</span>
                    {h}
                  </li>
                ))}
              </ul>
              <button 
                className="ignis-btn ignis-btn--tier" 
                style={{ borderColor: t.color, color: t.color }}
                onClick={() => navigate(user ? '/crypto' : '/signin')}
              >
                Get {t.name}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="ignis-cta">
        <h2 className="ignis-cta__title">Ready to let AI trade for you?</h2>
        <p className="ignis-cta__desc">
          Join Ignis today and experience what AI-powered crypto trading should feel like.
        </p>
        <button className="ignis-btn ignis-btn--primary ignis-btn--large" onClick={() => navigate(user ? '/crypto' : '/signin')}>
          Start Trading with Ignis AI Agent
        </button>
      </section>

    </div>
  );
};

export default IgnisLanding;